import { Button } from '@rocco/components/button';
import { Input } from '@rocco/components/input';
import { Label } from '@rocco/components/label';
import { z } from 'zod';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowLeftIcon } from '@rocco/icons/svg/arrow/arrow-left.svg';

export const confirmationDetailsSchema = z.object({
    clientName: z
        .string()
        .trim()
        .min(1, { message: 'Please enter your name' })
        .max(37, { message: 'Name must be 37 characters or fewer' })
        .regex(/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/, {
            message:
                'Name must contain only letters, single spaces, single hyphens, or apostrophes',
        }),
    clientEmail: z
        .string()
        .trim()
        .email({ message: 'Please enter a valid email address' }),
    clientCompany: z
        .string()
        .trim()
        .max(50, { message: 'Company name must be 50 characters or fewer' }),
});

export type ConfirmationDetails = z.infer<typeof confirmationDetailsSchema>;

interface ConfirmationPhaseProps {
    variant?: 'light' | 'dark';
    meetingSummary: React.ReactNode;
    clientName: string;
    clientEmail: string;
    clientCompany: string;
    onClientNameChange: (value: string) => void;
    onClientEmailChange: (value: string) => void;
    onClientCompanyChange: (value: string) => void;
    userInputsDisabled: boolean;
    companyInputDisabled: boolean;
    userEmailLabel: string;
    /** When false the details inputs are hidden and the stored booking details are reused */
    showDetailsForm?: boolean;
    showValidationErrors: boolean;
    fieldErrors: {
        clientName?: string[];
        clientEmail?: string[];
        clientCompany?: string[];
    };
    onBack: () => void;
    onConfirm: () => void;
    confirmDisabled: boolean;
    isProcessing: boolean;
    buttonLabel: string;
    statusMessage?: string;
    showStatusMessage: boolean;
    showSpamHint?: boolean;
    /**
     * Let the light card fill its container instead of the fixed width and
     * height the standalone /booking page needs. The inline accordion card is
     * narrower than that page, so the fixed sizing overflows it and clips the
     * buttons. Off by default, so /booking is unchanged. See BC-5174.
     */
    fluid?: boolean;
    /**
     * Optional note shown while the booking is submitting (dark variant only).
     * The submit hits the organiser's calendar provider and can take several
     * seconds, so callers can explain the wait (e.g. "Confirming with calendar
     * provider…"). See BC-4603.
     */
    processingHint?: string;
}

/**
 * Displays the confirmation form for booking details. Supports `variant="dark"`
 * for the revamped project detail page's inline dark booking card.
 */
export const ConfirmationPhase = ({
    variant = 'light',
    meetingSummary,
    clientName,
    clientEmail,
    clientCompany,
    onClientNameChange,
    onClientEmailChange,
    onClientCompanyChange,
    userInputsDisabled,
    companyInputDisabled,
    userEmailLabel,
    showDetailsForm = true,
    showValidationErrors,
    fieldErrors,
    onBack,
    onConfirm,
    confirmDisabled,
    isProcessing,
    buttonLabel,
    statusMessage,
    showStatusMessage,
    showSpamHint = false,
    fluid = false,
    processingHint,
}: ConfirmationPhaseProps) => {
    const nameErrorMessage = fieldErrors.clientName?.[0];
    const emailErrorMessage = fieldErrors.clientEmail?.[0];
    const companyErrorMessage = fieldErrors.clientCompany?.[0];

    const trimmedClientName = clientName.trim();
    const trimmedClientEmail = clientEmail.trim();
    const trimmedClientCompany = clientCompany.trim();

    const shouldShowNameError =
        Boolean(nameErrorMessage) &&
        (showValidationErrors || trimmedClientName.length > 37);
    const shouldShowEmailError =
        Boolean(emailErrorMessage) &&
        (showValidationErrors || trimmedClientEmail.length > 0);
    const shouldShowCompanyError =
        Boolean(companyErrorMessage) &&
        (showValidationErrors || trimmedClientCompany.length > 0);

    if (variant === 'dark') {
        return (
            <div className="flex gap-[3.84rem] max-md:flex-col">
                {meetingSummary}
                <div className="flex flex-1 flex-col gap-[1.44rem]">
                    <div>
                        <label
                            htmlFor="booking-user-name"
                            className="mb-[0.576rem] block text-[0.864rem] font-medium text-white"
                        >
                            Your Name*
                        </label>
                        <Input
                            id="booking-user-name"
                            maxLength={37}
                            value={clientName}
                            onChange={(event) =>
                                onClientNameChange(event.target.value)
                            }
                            disabled={userInputsDisabled}
                            placeholder="Enter your full name"
                            className="mt-2 w-full"
                            autoComplete="name"
                            color="gray"
                            required
                            aria-invalid={
                                shouldShowNameError ? true : undefined
                            }
                            aria-describedby={
                                shouldShowNameError
                                    ? 'booking-user-name-error'
                                    : undefined
                            }
                        />
                        {shouldShowNameError ? (
                            <p
                                id="booking-user-name-error"
                                className="mt-[0.288rem] text-[0.72rem] text-red-400"
                            >
                                {nameErrorMessage}
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <label
                            htmlFor="booking-user-email"
                            className="mb-[0.576rem] block text-[0.864rem] font-medium text-white"
                        >
                            Your Email*
                        </label>
                        <Input
                            id="booking-user-email"
                            type="email"
                            value={clientEmail}
                            onChange={(event) =>
                                onClientEmailChange(event.target.value)
                            }
                            disabled={userInputsDisabled}
                            placeholder={userEmailLabel}
                            className="mt-2"
                            autoComplete="email"
                            color="gray"
                            required
                            aria-invalid={
                                shouldShowEmailError ? true : undefined
                            }
                            aria-describedby={
                                shouldShowEmailError
                                    ? 'booking-user-email-error'
                                    : undefined
                            }
                        />
                        {shouldShowEmailError ? (
                            <p
                                id="booking-user-email-error"
                                className="mt-[0.288rem] text-[0.72rem] text-red-400"
                            >
                                {emailErrorMessage}
                            </p>
                        ) : null}
                        {showSpamHint ? (
                            <p className="text-amber-400 mt-[0.48rem] text-[0.72rem]">
                                <span className="font-semibold">
                                    Didn't receive a calendar invite?
                                </span>{' '}
                                Please check your spam or junk folder.
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <label
                            htmlFor="booking-user-company"
                            className="mb-[0.576rem] block text-[0.864rem] font-medium text-white"
                        >
                            Your Company
                        </label>
                        <Input
                            id="booking-user-company"
                            maxLength={50}
                            value={clientCompany}
                            onChange={(event) =>
                                onClientCompanyChange(event.target.value)
                            }
                            disabled={companyInputDisabled}
                            placeholder="Company name (optional)"
                            className="mt-2"
                            autoComplete="organization"
                            color="gray"
                            aria-invalid={
                                shouldShowCompanyError ? true : undefined
                            }
                            aria-describedby={
                                shouldShowCompanyError
                                    ? 'booking-user-company-error'
                                    : undefined
                            }
                        />
                        {shouldShowCompanyError ? (
                            <p
                                role="alert"
                                id="booking-user-company-error"
                                className="mt-[0.288rem] text-[0.72rem] text-red-400"
                            >
                                {companyErrorMessage}
                            </p>
                        ) : null}
                    </div>

                    {showStatusMessage ? (
                        <div
                            role="alert"
                            className="rounded-[0.384rem] border border-red-500/30 bg-red-500/10 px-[0.96rem] py-[0.72rem] text-[0.768rem] text-red-400"
                        >
                            {statusMessage}
                        </div>
                    ) : null}

                    {isProcessing && processingHint ? (
                        <div className="text-center text-[0.768rem] text-white/60">
                            {processingHint}
                        </div>
                    ) : null}

                    <div className="flex gap-[0.96rem] pt-[0.48rem]">
                        <Button
                            type="button"
                            onClick={onBack}
                            className="h-[2.496rem] flex-1 rounded-[var(--radius-btn-m)] text-[0.768rem]"
                            color="outline"
                            inverted
                        >
                            <ArrowLeftIcon className="mr-1 h-[0.72rem] w-[0.72rem]" />{' '}
                            Back
                        </Button>
                        <Button
                            type="button"
                            onClick={onConfirm}
                            disabled={confirmDisabled}
                            className="h-[2.496rem] flex-1 rounded-[var(--radius-btn-m)] text-[0.768rem]"
                            color="white"
                            isLoading={isProcessing}
                        >
                            {buttonLabel}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Light variant (default) — used in the booking modal
    const confirmationCard = (
        <div
            className={
                fluid
                    ? 'flex w-full items-start gap-10 max-md:flex-col'
                    : 'flex w-200 items-center gap-20'
            }
        >
            {meetingSummary}
            <div
                className={
                    fluid
                        ? 'relative min-w-0 flex-1 space-y-6'
                        : 'relative h-108 w-87 space-y-6'
                }
            >
                {!showDetailsForm ? (
                    <p className="text-sm text-gray-700">
                        We'll keep the details from your original booking.
                    </p>
                ) : (
                    <>
                        <div>
                            <Label
                                htmlFor="booking-user-name"
                                className="text-sm font-medium text-gray-700"
                            >
                                Your Name*
                            </Label>
                            <Input
                                id="booking-user-name"
                                maxLength={37}
                                value={clientName}
                                onChange={(event) =>
                                    onClientNameChange(event.target.value)
                                }
                                disabled={userInputsDisabled}
                                placeholder="Enter your full name"
                                className={`mt-2 w-full ${userInputsDisabled ? 'overflow-hidden rounded-2 bg-gray-100' : ''}`}
                                autoComplete="name"
                                color="lightOutline"
                                required
                                aria-invalid={
                                    shouldShowNameError ? true : undefined
                                }
                                aria-describedby={
                                    shouldShowNameError
                                        ? 'booking-user-name-error'
                                        : undefined
                                }
                            />
                            {shouldShowNameError ? (
                                <p
                                    id="booking-user-name-error"
                                    className="mt-1 h-0 text-sm text-[red]"
                                >
                                    {nameErrorMessage}
                                </p>
                            ) : null}
                        </div>
                        <div>
                            <Label
                                htmlFor="booking-user-email"
                                className="text-sm font-medium text-gray-700"
                            >
                                Your Email*
                            </Label>
                            <Input
                                id="booking-user-email"
                                type="email"
                                value={clientEmail}
                                onChange={(event) =>
                                    onClientEmailChange(event.target.value)
                                }
                                disabled={userInputsDisabled}
                                placeholder={userEmailLabel}
                                className={`mt-2 ${userInputsDisabled ? 'overflow-hidden rounded-2 bg-gray-100' : ''}`}
                                autoComplete="email"
                                color="lightOutline"
                                required
                                aria-invalid={
                                    shouldShowEmailError ? true : undefined
                                }
                                aria-describedby={
                                    shouldShowEmailError
                                        ? 'booking-user-email-error'
                                        : undefined
                                }
                            />
                            {shouldShowEmailError ? (
                                <p
                                    id="booking-user-email-error"
                                    className="mt-1 h-0 text-sm text-[red]"
                                >
                                    {emailErrorMessage}
                                </p>
                            ) : null}
                            {showSpamHint && (
                                <div className="mt-2 text-xs text-brick-600">
                                    <p className="font-semibold">
                                        Didn't Receive a calendar invite?
                                    </p>
                                    <p>
                                        Please check your spam or junk folder.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div>
                            <Label
                                htmlFor="booking-user-company"
                                className="text-sm font-medium text-gray-700"
                            >
                                Your Company
                            </Label>
                            <Input
                                id="booking-user-company"
                                maxLength={50}
                                value={clientCompany}
                                onChange={(event) =>
                                    onClientCompanyChange(event.target.value)
                                }
                                disabled={companyInputDisabled}
                                placeholder="Company name (optional)"
                                className={`mt-2 ${companyInputDisabled ? 'overflow-hidden rounded-2 bg-gray-100' : ''}`}
                                autoComplete="organization"
                                color="lightOutline"
                                aria-invalid={
                                    shouldShowCompanyError ? true : undefined
                                }
                                aria-describedby={
                                    shouldShowCompanyError
                                        ? 'booking-user-company-error'
                                        : undefined
                                }
                            />
                            {shouldShowCompanyError ? (
                                <p
                                    role="alert"
                                    id="booking-user-company-error"
                                    className="mt-1 h-0 text-sm text-[red]"
                                >
                                    {companyErrorMessage}
                                </p>
                            ) : null}
                        </div>
                    </>
                )}
                <div className="absolute right-0 bottom-0 flex h-11 w-full justify-center gap-2 pt-6">
                    <Button
                        type="button"
                        onClick={onBack}
                        className="h-11 flex-1 items-center justify-center"
                        color="outline"
                    >
                        <ArrowLeftIcon className="mr-1 h-3 w-3" /> Back
                    </Button>
                    <Button
                        type="button"
                        onClick={onConfirm}
                        disabled={confirmDisabled}
                        className="h-11 flex-1"
                        isLoading={isProcessing}
                    >
                        {buttonLabel}
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <div
            className={cn(
                'flex w-full flex-col',
                // The modal gives this a bounded height and scrolls inside it.
                // The inline card has no such bound, so scrolling there just
                // hides the buttons; let it grow instead.
                !fluid && 'h-full',
            )}
        >
            <div className={cn('relative flex-1', !fluid && 'overflow-y-auto')}>
                {confirmationCard}
            </div>
            {showStatusMessage ? (
                <div
                    className="border-rose-200 bg-rose-50 text-rose-700 mt-4 rounded-2 border px-4 py-3 text-sm"
                    role="alert"
                >
                    {statusMessage}
                </div>
            ) : null}
        </div>
    );
};
